import { hasProductAvailabilityData } from "./productAvailability";

const isObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const getWishlistSource = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.wishlist)) return value.wishlist;
  return [];
};

const nestedProductKeys = ["product", "productData", "productId"];
const nestedVariantKeys = ["selectedVariant", "variant"];

const getFirstNestedObject = (item, keys) =>
  keys.map((key) => item?.[key]).find(isObject) || null;

export const normalizeWishlistProduct = (item) => {
  if (!isObject(item)) return item;

  const nestedProduct = getFirstNestedObject(item, nestedProductKeys);
  const nestedVariant = getFirstNestedObject(item, nestedVariantKeys);

  if (!nestedProduct && !nestedVariant) return item;

  const normalized = {
    ...item,
    ...(nestedProduct || {}),
    ...(nestedVariant || {}),
  };

  if (isObject(item.productId)) {
    normalized.productId = item.productId._id || item.productId.id || "";
  }

  return normalized;
};

export const getWishlistProductId = (productOrId) => {
  if (typeof productOrId === "string" || typeof productOrId === "number") {
    return String(productOrId).trim();
  }

  const product = normalizeWishlistProduct(productOrId);
  if (!isObject(product)) return "";

  return String(
    product._id ||
      product.id ||
      (isObject(product.productId)
        ? product.productId._id || product.productId.id
        : product.productId) ||
      "",
  ).trim();
};

export const normalizeWishlistPayload = (value) => {
  const seen = new Set();

  return getWishlistSource(value)
    .map(normalizeWishlistProduct)
    .filter((item) => {
      const id = getWishlistProductId(item);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
};

const toAvailabilityNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? Math.max(0, numberValue) : null;
};

export const enrichWishlistAvailability = async (
  value,
  fetchAvailabilityByProductCode,
) => {
  const wishlist = normalizeWishlistPayload(value);

  if (typeof fetchAvailabilityByProductCode !== "function") {
    return wishlist;
  }

  const productCodes = [
    ...new Set(
      wishlist
        .filter(
          (product) => isObject(product) && !hasProductAvailabilityData(product),
        )
        .map((product) => product.productCode)
        .filter((productCode) => productCode != null && productCode !== "")
        .map(String),
    ),
  ];

  if (!productCodes.length) return wishlist;

  const availabilityEntries = await Promise.all(
    productCodes.map(async (productCode) => {
      try {
        const totalAvailable = await fetchAvailabilityByProductCode(productCode);
        return [productCode, toAvailabilityNumber(totalAvailable)];
      } catch {
        return [productCode, null];
      }
    }),
  );

  const availabilityByProductCode = new Map(
    availabilityEntries.filter(([, totalAvailable]) => totalAvailable !== null),
  );

  return wishlist.map((product) => {
    if (
      !isObject(product) ||
      hasProductAvailabilityData(product) ||
      !availabilityByProductCode.has(String(product.productCode))
    ) {
      return product;
    }

    return {
      ...product,
      totalAvailable: availabilityByProductCode.get(String(product.productCode)),
    };
  });
};
